import { ethers } from 'ethers'
import localeAbi from '@/connect/abi/index.js'
import { ErrorInfo, errCodeHandle } from '@/tools/error'
import TrezorConnect from 'trezor-connect'
import * as HDKey from 'hdkey'
import { publicToAddress, bufferToHex } from 'ethereumjs-util'
// import EthereumTx from 'ethereumjs-tx'

TrezorConnect.manifest({
  email: 'developer@pundix.com',
  appUrl: 'https://www.pundix.com'
})

const abi = localeAbi[process.env.VUE_APP_CHAIN]

import { Wallet } from '@/connect/wallet/wallet'

import UAParser from 'ua-parser-js'

// 路径类型
const pathTypeArray = [
  {
    type: "m/44'/60'/0'/0",
    placeholder: "m/44'/60'/0'/0/i"
  }
  // {
  //   type: "m/44'/60'",
  //   placeholder: "m/44'/60'/i'/0/0"
  // }
]

const pathTypeObj = {}

pathTypeArray.forEach(item => {
  pathTypeObj[item.type] = item.placeholder
})

function throwError(code, error) {
  switch (code) {
    case 'browser_not_support':
      errCodeHandle.go(250001)
      throw ErrorInfo(250001)
    case 'open_failed':
      errCodeHandle.go(250002)
      throw ErrorInfo(250002)
    case 'Init_IframeTimeout':
      errCodeHandle.go(250003)
      throw ErrorInfo(250003)
    case 'Method_Interrupted':
      errCodeHandle.go(250004)
      throw ErrorInfo(250004)
    case 'Method_PermissionsNotGranted':
      errCodeHandle.go(250005)
      throw ErrorInfo(250005)
    case 'Method_Cancel':
      errCodeHandle.go(250006)
      throw ErrorInfo(250006)
    case 'Init_ManifestMissing':
      errCodeHandle.go(250007)
      throw ErrorInfo(250007)
    case 'Device_CallInProgress':
      errCodeHandle.go(250008)
      throw ErrorInfo(250008)
    case 'Failure_ActionCancelled':
      errCodeHandle.go(250009)
      throw ErrorInfo(250009)
    case 'Transport_Missing':
      errCodeHandle.go(250010)
      throw ErrorInfo(250010)
    case 'Device_UsedElsewhere':
      errCodeHandle.go(250011)
      throw ErrorInfo(250011)
    case undefined:
      errCodeHandle.go(250012)
      throw ErrorInfo(250012)
    default:
      console.log(error)
      errCodeHandle.go(400001)
      throw ErrorInfo(400001)
  }
}

export class Trezor extends Wallet {
  constructor() {
    super()
    this._paths = {}
    this.publicKey = {}
    this.PATHTYPEARRAY = pathTypeArray
    this.PATHTYPEOBJ = pathTypeObj
  }

  async connect() {
    const parser = new UAParser()
    const browser = parser.getBrowser()
    if (browser.name !== 'Chrome' && browser.name !== 'Firefox') {
      throwError('browser_not_support')
    }

    this.chainId = process.env.VUE_APP_CHAIN
    this._connector = TrezorConnect
    const account = await this.getAccount(this.PATHTYPEARRAY[0].type, 0)
    this.account = account.address
    this._getEthers()
  }

  /**
   * 断开连接 重置钱包
   */
  async disconnect() {
    this._initWallet()
  }

  /**
   * 获取chain Id
   * @return {Promise<number>}
   */
  async getChainId() {
    return this.chainId
  }

  // 获取提供者和签名者
  _getEthers() {
    this._provider = new ethers.providers.JsonRpcProvider(
      process.env.VUE_APP_RPC
      // process.env.VUE_APP_CHAIN
    )
    this._signer = this._provider.getSigner()
  }

  /**
   * 批准配额
   * @param {String} ERC20 - ERC20地址
   * @param {String} spender - 花费者地址
   * @param {String} [amount] - 要配额的金额 单位wei 默认为最大值
   * @return {Promise} 结果
   */
  approve(ERC20, spender, amount = '0x' + 'f'.repeat(64)) {
    // const contract = new ethers.Contract(ERC20, CommonERC20, this._signer)
    // return contract.approve(spender, amount)
    const iface = new ethers.utils.Interface(abi.commonERC20)
    const data = iface.encodeFunctionData('approve', [spender, amount])

    const tx = {
      from: this.account,
      to: ERC20,
      value: '0x00',
      data: data
    }

    return this.sendTx(tx)
  }

  /**
   * 获取路径
   * @param {String} type - 路径类型  值在 PATHTYPEARRAY 里定义
   * @param {Number} index - 路径变量  >=0 int类型
   * @return {String} 路径值
   * @private
   */
  _getPath(type, index) {
    return this.PATHTYPEOBJ[type].replace('i', index)
  }

  /**
   * 获取账户信息
   * @param {String} type - 路径类型  值在 PATHTYPEARRAY 里定义
   * @param {Number} index - 路径变量  >=0 int类型
   * @return {Promise<Object>} account - 账户信息
   * @return {String} account.address - 地址
   * @return {String} account.publicKey - 公钥
   */
  async getAccount(type, index) {
    if (this.publicKey[type] === undefined) {
      await this._setPath(type)
    }

    const hdKey = new HDKey()
    hdKey.publicKey = Buffer.from(this.publicKey[type].publicKey, 'hex')
    hdKey.chainCode = Buffer.from(this.publicKey[type].chainCode, 'hex')
    const derivedKey = hdKey.derive('m/' + index)
    const derivedAddress = publicToAddress(derivedKey.publicKey, true)
    const address = bufferToHex(derivedAddress)
    this._paths[address] = this._getPath(type, index)
    return { publicKey: derivedKey, address: address }
  }

  async _setPath(type) {
    if (this.publicKey[type] === undefined) {
      const result = await TrezorConnect.ethereumGetPublicKey({
        path: type
      })
      console.log('result', result)
      if (!result.payload) {
        throwError('open_failed', result)
      }
      if (!result.success) {
        throwError(result.payload.code, result)
      }
      this.publicKey[type] = result.payload
    }
  }

  /**
   * 设置当前地址
   * @param address
   */
  setAccount(address) {
    this.account = address
  }

  /**
   * 发交易
   * @param {Object} data - 交易体
   * @param {string} data.from - 发出者地址
   * @param {string} data.value - eth数量 wei
   * @param {string} data.to - 接收者地址
   * @param {string} data.data - 合约数据 uint256
   * @param {string} [data.gasPrice] - gas Price 单位wei
   * @param {string} [data.nonce] - nonce
   * @param {string} [data.gas] - gas
   * @return {Promise} - 交易成功或失败
   */
  async sendTx(data) {
    if (data.nonce === undefined) {
      data.nonce = await this.getNonce()
    }
    // trezor 的nonce 必须是hex类型  ledger是string类型
    data.nonce = '0x' + Number(data.nonce).toString(16)
    if (data.gasPrice === undefined) {
      const gasPrice = await this.getGasPrice()
      data.gasPrice = gasPrice.toHexString()
    }
    if (data.gas === undefined) {
      const gas = await this.estimateGas(data)
      data.gas = gas.toHexString()
    }
    const txData = JSON.parse(JSON.stringify(data))
    delete txData.from
    delete txData.gas

    // 小狐狸的gasLimit 和 gas 是反的  这里要换回来
    txData.gasLimit = data.gas

    // const txHex = ethers.utils.serializeTransaction(txData)
    // 签名
    const signature = await this._connector.ethereumSignTransaction({
      path: this._paths[data.from],
      transaction: txData
    })
    if (!signature.payload) {
      throwError('open_failed', signature)
    }
    if (!signature.success) {
      throwError(signature.payload.code, signature)
    }

    console.log('signature', signature)
    const sign = {
      v: Number(signature.payload.v),
      r: signature.payload.r,
      s: signature.payload.s
    }

    console.log('txData', txData)
    // 序列化的nonce 必须是number类型
    // txData.nonce = Number(txData.nonce)
    const tx = ethers.utils.serializeTransaction(txData, sign)

    // const parseTx = ethers.utils.parseTransaction(tx)
    // console.log('parseTx', parseTx)
    // 发交易
    return new Promise((resolve, reject) => {
      this._provider
        .sendTransaction(tx)
        .then(res => {
          resolve(res.hash)
        })
        .catch(err => {
          reject(err)
        })
    })
  }
}
